import { useRef, useState } from "react";
import { enviarArquivos } from "../../lib/api.js";
import { reduzirImagem } from "../../lib/imagem.js";
import { Botao, Falha, Girando, cx } from "../../components/ui.js";

export interface FotoDoLote {
  path: string;
  url: string;
  nomeOriginal: string;
}

/** Quantas sobem ao mesmo tempo. Três é o que o navegador atende sem enfileirar. */
const EM_PARALELO = 3;

interface Envio {
  nome: string;
  estado: "esperando" | "enviando" | "pronta" | "falhou";
  percent: number;
  erro: string | null;
  foto: FotoDoLote | null;
  arquivo: File;
}

/**
 * Seleção e envio das fotos do lote.
 *
 * **Uma requisição por foto**, e não uma leva com todas. A rota de acervo já
 * aceita vinte arquivos num `POST` só, mas ali o feedback é agregado: uma barra
 * e uma mensagem de erro para o conjunto. Num lote de quarenta fotos tiradas no
 * celular, a foto 38 falhar por tamanho derrubaria as trinta e nove outras — e
 * o operador não saberia qual delas foi.
 *
 * Aqui cada foto tem a sua barra, o seu erro e o seu "tentar de novo". O custo é
 * quarenta requisições em vez de uma; a rota é a mesma do atendimento, que já
 * faz exatamente isso quarenta vezes por evento.
 */
export function EnviarFotos({
  fotos,
  aoMudar,
}: {
  fotos: FotoDoLote[];
  aoMudar: (fotos: FotoDoLote[]) => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [envios, setEnvios] = useState<Envio[]>([]);

  function atualizar(indice: number, mudanca: Partial<Envio>) {
    setEnvios((atual) => atual.map((e, i) => (i === indice ? { ...e, ...mudanca } : e)));
  }

  async function subirUma(envio: Envio, indice: number, acumulado: FotoDoLote[]) {
    atualizar(indice, { estado: "enviando", percent: 0, erro: null });

    try {
      const reduzida = await reduzirImagem(envio.arquivo);

      const r = await enviarArquivos<{ path: string; url: string }>(
        "/api/generations/upload-photo",
        { nome: "photo", arquivos: [reduzida] },
        (p) => atualizar(indice, { percent: p.percent }),
      );

      const foto: FotoDoLote = { ...r, nomeOriginal: envio.nome };
      atualizar(indice, { estado: "pronta", percent: 100, foto });
      acumulado.push(foto);
    } catch (e) {
      atualizar(indice, {
        estado: "falhou",
        erro: e instanceof Error ? e.message : "Não foi possível enviar",
      });
    }
  }

  /** Sobe a fila com paralelismo limitado, preservando a ordem de escolha. */
  async function subir(lista: Envio[], base: FotoDoLote[]) {
    const prontas = [...base];
    let proximo = 0;

    async function trabalhador() {
      for (;;) {
        const i = proximo++;
        const envio = lista[i];
        if (envio === undefined) return;
        // O índice no estado é o da lista inteira, não o desta fatia.
        await subirUma(envio, base.length + i, prontas);
      }
    }

    await Promise.all(Array.from({ length: Math.min(EM_PARALELO, lista.length) }, trabalhador));

    aoMudar(prontas);
  }

  async function receber(arquivos: FileList | null) {
    if (!arquivos || arquivos.length === 0) return;

    const novos: Envio[] = Array.from(arquivos).map((arquivo) => ({
      nome: arquivo.name,
      estado: "esperando",
      percent: 0,
      erro: null,
      foto: null,
      arquivo,
    }));

    setEnvios((atual) => [...atual, ...novos]);
    await subir(novos, fotos);
  }

  async function tentarDeNovo(indice: number) {
    const envio = envios[indice];
    if (envio === undefined) return;

    const prontas = envios.filter((e, i) => i !== indice && e.foto !== null).map((e) => e.foto!);

    await subirUma(envio, indice, prontas);
    aoMudar(prontas);
  }

  const enviando = envios.some((e) => e.estado === "enviando" || e.estado === "esperando");
  const falharam = envios.filter((e) => e.estado === "falhou").length;

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={entrada}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => {
          void receber(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Botao tom="quieto" carregando={enviando} onClick={() => entrada.current?.click()}>
          {envios.length === 0 ? "Escolher as fotos" : "Acrescentar mais"}
        </Botao>

        {envios.length > 0 && (
          <span className="text-[0.8125rem] text-texto-suave">
            <span className="num font-medium text-texto">{fotos.length}</span> de{" "}
            <span className="num">{envios.length}</span> enviadas
            {falharam > 0 && <span className="text-descartar"> · {falharam} falharam</span>}
          </span>
        )}
      </div>

      {envios.length > 0 && (
        <ul className="flex max-h-80 flex-col gap-1.5 overflow-y-auto">
          {envios.map((e, i) => (
            <li
              key={`${e.nome}-${i}`}
              className="flex items-center gap-3 rounded-peca border border-borda bg-superficie px-3 py-2"
            >
              <div className="size-10 shrink-0 overflow-hidden rounded-peca bg-superficie-alta">
                {e.foto && (
                  <img src={e.foto.url} alt="" className="size-full object-cover" loading="lazy" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.8125rem] text-texto">{e.nome}</p>

                {e.estado === "falhou" ? (
                  <p className="truncate text-[0.75rem] text-descartar">{e.erro}</p>
                ) : (
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-borda">
                    <div
                      className={cx(
                        "h-full rounded-full transition-[width]",
                        e.estado === "pronta" ? "bg-fixado" : "bg-brasa",
                      )}
                      style={{ width: `${e.estado === "pronta" ? 100 : e.percent}%` }}
                    />
                  </div>
                )}
              </div>

              {e.estado === "enviando" && <Girando className="size-4 shrink-0 text-texto-fraco" />}
              {e.estado === "falhou" && (
                <Botao tom="fantasma" formato="nu" onClick={() => void tentarDeNovo(i)}>
                  Tentar de novo
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}

      {falharam > 0 && !enviando && (
        <Falha
          mensagem={`${falharam} foto${falharam > 1 ? "s" : ""} não subiu. O lote pode ser criado sem elas, ou tente de novo em cada uma.`}
        />
      )}
    </div>
  );
}
